import styles from "./styles/vocal-remover-result.module.scss";
import generatingIcon from "@/assets/icons/generating-icon.svg";
import Image from "next/image";
import { useDownload } from "@/hooks/use-download";
import VocalRemoverPlayer from "@/components/pages/vocal-remover/components/vocal-remover-player";
import FilledButton from "@/components/common/filled-button";
import UnfilledButton from "@/components/common/unfilled-button";
import { useMobile } from "@/hooks/use-responsive";
import React, { useEffect, useState } from "react";
import { formatBlobDuration, getBlobDurationInSecs } from "@/utils/blob-utils";
import { useMusicPlayer } from "@/providers/music-provider/music-provider";
import { useProfileContext } from "@/providers/profile-provider/profile-provider";
import UpgradeToDownload from "@/components/common/upgrade-to-download";
import { vocalRemoverLicenseManager } from "@/services/license/license-managers/vocal-remover-license-manager";
import ShareCoverButton from "@/components/common/share-cover-button";
import shareIcon from "@/assets/icons/share-icon.svg";

type VocalRemoverPlaylistItem = {
  url: string;
  title: string;
  borderColor: string;
};

interface VocalRemoverResultProps {
  id?: string;
  originalFile: File;
  vocalUrl: string;
  instrumentalUrl: string;
  setStatus: () => void;
}

const VocalRemoverResult = ({
  id,
  originalFile,
  vocalUrl,
  instrumentalUrl,
  setStatus,
}: VocalRemoverResultProps) => {
  const [playlist, setPlaylist] = useState<VocalRemoverPlaylistItem[]>([]);
  const [fileDurationString, setFileDurationString] = useState("");
  const [originalFileUrl, setOriginalFileUrl] = useState("");
  const { downloadMultiple } = useDownload();
  const { isMobile } = useMobile();
  const buttonPadding = isMobile ? "9px 20px" : "12px 24px";
  const { license, isLoading } = useProfileContext();

  useEffect(() => {
    const getFileDuration = async () => {
      const durationSeconds = await getBlobDurationInSecs(originalFile);
      return formatBlobDuration(durationSeconds);
    };

    getFileDuration().then((duration) => {
      setFileDurationString(duration);
      const originalUrl = URL.createObjectURL(originalFile);
      setOriginalFileUrl(originalUrl);
      setPlaylist([
        { url: originalUrl, title: "Original", borderColor: "#FF5722" },
        { url: vocalUrl, title: "Vocal", borderColor: "#F9A825" },
        { url: instrumentalUrl, title: "Instrumental", borderColor: "#F2F2F2" },
      ]);
    });
  }, [instrumentalUrl, originalFile, vocalUrl]);

  const handleSaveAll = async () => {
    const downloadData = [
      {
        url: vocalUrl,
        title: getDownloadTitle("Vocal", originalFile.name),
      },
      {
        url: instrumentalUrl,
        title: getDownloadTitle("Instrumental", originalFile.name),
      },
    ];
    await downloadMultiple(downloadData);
  };
  const { playTrack } = useMusicPlayer();

  const handlePlayTrack = (index: number) => {
    const playlist = [originalFileUrl, vocalUrl, instrumentalUrl];
    playTrack(playlist[index], index, playlist);
  };

  return (
    <div className={styles.result_wrapper}>
      <div className={styles.header}>
        <Image src={generatingIcon} alt={""} />
        <div>
          {originalFile.name} - {fileDurationString}
        </div>
      </div>
      {playlist.map((x, index) => (
        <VocalRemoverPlayer
          key={x.title}
          url={x.url}
          title={x.title}
          downloadTitle={getDownloadTitle(x.title, originalFile.name)}
          borderColor={x.borderColor}
          onPlay={() => handlePlayTrack(index)}
          license={license}
          isLoadingLicense={isLoading}
          shareButton={
            <ShareCoverButton
              featureType="vocal-remover"
              contentId={id}
              url={x.url}
            >
              <Image src={shareIcon} alt={""} />
            </ShareCoverButton>
          }
        />
      ))}
      <div className={styles.buttons_container}>
        {vocalRemoverLicenseManager.downloadResultLimiter.isDownloadEnabled(
          license,
        ) &&
          !isLoading && (
            <FilledButton
              text={"SAVE ALL"}
              padding={buttonPadding}
              backgroundColor={"#FF5722"}
              color={"#F2F2F2"}
              onClick={handleSaveAll}
            />
          )}

        <UnfilledButton
          text={"NEW TASK"}
          padding={buttonPadding}
          borderColor={"#F9A825"}
          onClick={setStatus}
        />
      </div>
      <div className={styles.upgrade_to_download_wrapper}>
        <UpgradeToDownload license={license} isLoading={isLoading} />
      </div>
    </div>
  );
};

const getDownloadTitle = (type: string, fileName: string) => {
  return `${type} - ${fileName}`;
};

export default VocalRemoverResult;
